import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { format, startOfToday } from "date-fns";
import {
  CalendarDays,
  Loader2,
  MapPin,
  PawPrint,
  Send,
  Plus,
  Minus,
  Briefcase,
} from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { SERVICE_OPTIONS, CARD_TO_SERVICE } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const PET_ROWS = [
  { key: "puppy", label: "Puppy", sub: "Under 6 months" },
  { key: "small", label: "Small dog", sub: "Less than 10 kg" },
  { key: "medium", label: "Medium dog", sub: "11–20 kg" },
  { key: "large", label: "Large dog", sub: "21–40 kg" },
];

const inputCls =
  "bg-background border-border text-primary !text-base placeholder:text-primary/75 focus-visible:ring-ring h-12 rounded-xl";

const fieldBtn =
  "w-full flex items-center gap-3 bg-background border border-border rounded-xl h-12 px-3.5 text-left transition-colors hover:border-accent";

function petsSummary(pets) {
  const labels = [
    [pets.puppy, "puppy", "puppies"],
    [pets.small, "small dog", "small dogs"],
    [pets.medium, "medium dog", "medium dogs"],
    [pets.large, "large dog", "large dogs"],
  ];
  const parts = labels
    .filter(([n]) => n > 0)
    .map(([n, s, p]) => `${n} ${n === 1 ? s : p}`);
  return parts.join(", ");
}

export default function Booking() {
  const [service, setService] = useState(null);
  const [pets, setPets] = useState({ puppy: 0, small: 0, medium: 0, large: 0 });
  const [frequency, setFrequency] = useState("one-off");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateUntil, setDateUntil] = useState(null);
  const [oneOffDates, setOneOffDates] = useState([]);
  const [weekdays, setWeekdays] = useState([]);
  const [startWeek, setStartWeek] = useState(null);
  const [owner, setOwner] = useState({ owner_name: "", email: "", phone: "", suburb: "", notes: "" });

  const [serviceOpen, setServiceOpen] = useState(false);
  const [petsOpen, setPetsOpen] = useState(false);
  const [daysOpen, setDaysOpen] = useState(false);
  const [fromOpen, setFromOpen] = useState(false);
  const [untilOpen, setUntilOpen] = useState(false);
  const [oneOffOpen, setOneOffOpen] = useState(false);
  const [startWeekOpen, setStartWeekOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      const svcId = CARD_TO_SERVICE[e.detail] || e.detail;
      const opt = SERVICE_OPTIONS.find((s) => s.id === svcId);
      if (opt) setService(opt);
    };
    window.addEventListener("prefill-service", handler);
    return () => window.removeEventListener("prefill-service", handler);
  }, []);

  const isOvernight = service?.overnight;
  const showHowOften = service && !service.overnight;
  const petTotal = pets.puppy + pets.small + pets.medium + pets.large;

  const petsLabel = petTotal > 0 ? petsSummary(pets) : "Your dog(s)";
  const oneOffLabel = useMemo(() => {
    if (!oneOffDates.length) return "Add days";
    return [...oneOffDates].sort((a, b) => a - b).map((d) => format(d, "d MMM")).join(", ");
  }, [oneOffDates]);
  const weekdaysLabel = weekdays.length
    ? WEEKDAYS.filter((d) => weekdays.includes(d)).map((d) => d.slice(0, 3)).join(", ")
    : "Add days";

  const setPet = (key, delta) =>
    setPets((p) => ({ ...p, [key]: Math.max(0, p[key] + delta) }));
  const setO = (k, v) => setOwner((o) => ({ ...o, [k]: v }));
  const toggleDay = (day) =>
    setWeekdays((w) => (w.includes(day) ? w.filter((d) => d !== day) : [...w, day]));

  const submit = async (e) => {
    e.preventDefault();
    if (!service) return toast.error("Please choose a service.");
    if (petTotal < 1) return toast.error("Please add at least one dog.");
    if (isOvernight && (!dateFrom || !dateUntil)) return toast.error("Please choose your From and End dates.");
    if (!isOvernight && frequency === "one-off" && oneOffDates.length < 1)
      return toast.error("Please add at least one day.");
    if (!isOvernight && frequency === "weekly" && (weekdays.length < 1 || !startWeek))
      return toast.error("Please choose your days and a start week.");
    if (!owner.owner_name || !owner.email || !owner.phone || !owner.suburb)
      return toast.error("Please fill in your contact details.");

    setLoading(true);
    try {
      const weekly = !isOvernight && frequency === "weekly";
      const oneOff = !isOvernight && frequency === "one-off";
      const payload = {
        service: service.name,
        pets,
        frequency: isOvernight ? null : frequency,
        date_from: isOvernight && dateFrom ? format(dateFrom, "PPP") : null,
        date_until: isOvernight && dateUntil ? format(dateUntil, "PPP") : null,
        one_off_dates: oneOff ? oneOffDates.map((d) => format(d, "PPP")) : [],
        weekdays: weekly ? WEEKDAYS.filter((d) => weekdays.includes(d)) : [],
        start_week: weekly && startWeek ? format(startWeek, "PPP") : null,
        ...owner,
      };
      await axios.post(`${API}/bookings`, payload);
      toast.success("Booking request sent! We'll be in touch by SMS or email shortly. 🐾");
      setService(null);
      setPets({ puppy: 0, small: 0, medium: 0, large: 0 });
      setFrequency("one-off");
      setDateFrom(null);
      setDateUntil(null);
      setOneOffDates([]);
      setWeekdays([]);
      setStartWeek(null);
      setOwner({ owner_name: "", email: "", phone: "", suburb: "", notes: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or SMS us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" className="py-24 sm:py-32 scroll-mt-20 bg-background">
      <div className="mx-auto max-w-2xl px-5 sm:px-8">
        <div className="text-center mb-12">
          <span className="text-accent font-bold text-sm tracking-widest uppercase">Book Now</span>
          <h2 className="font-display font-extrabold text-4xl sm:text-6xl mt-3 tracking-tight text-primary">
            Request a booking
          </h2>
          <p className="text-muted-foreground mt-4 max-w-lg mx-auto font-medium">
            Tell us what you need — we'll only ask for what's relevant, then confirm
            by SMS or email, usually within a couple of hours.
          </p>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          onSubmit={submit}
          data-testid="booking-form"
          className="rounded-3xl bg-card border border-black/5 shadow-[0_20px_60px_-25px_rgba(93,87,107,0.4)] p-6 sm:p-8 space-y-4"
        >
          {/* Service */}
          <button type="button" data-testid="booking-service-field" onClick={() => setServiceOpen(true)} className={fieldBtn}>
            <Briefcase size={18} className="text-accent shrink-0" />
            <span className={`text-base font-medium ${service ? "text-primary" : "text-primary/75"}`}>
              {service ? service.name : "Choose a service"}
            </span>
          </button>

          {/* Pets */}
          <button type="button" data-testid="booking-pets-field" onClick={() => setPetsOpen(true)} className={fieldBtn}>
            <PawPrint size={18} className="text-accent shrink-0" />
            <span className={`text-base font-medium ${petTotal > 0 ? "text-primary" : "text-primary/75"}`}>
              {petsLabel}
            </span>
          </button>

          {/* How often (only for non-overnight services) */}
          {showHowOften && (
            <div className="pt-2 text-center">
              <p className="font-display text-primary mb-3" style={{ fontSize: "1.4rem" }}>
                How often do you need it?
              </p>
              <div className="grid grid-cols-2 gap-3 max-w-sm mx-auto">
                {[
                  { id: "one-off", label: "One-off" },
                  { id: "weekly", label: "Repeat weekly" },
                ].map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    data-testid={`booking-frequency-${f.id}`}
                    onClick={() => setFrequency(f.id)}
                    className={`h-12 rounded-xl border-2 font-bold transition-colors ${
                      frequency === f.id
                        ? "border-accent bg-secondary/40 text-primary"
                        : "border-border bg-background text-primary/70 hover:border-accent"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Date fields — depend on service + frequency */}
          {(!service || isOvernight) && (
            <div className="grid grid-cols-2 gap-4">
              <DatePickerField testid="booking-from" placeholder="From" value={dateFrom} onSelect={setDateFrom} minDate={startOfToday()} open={fromOpen} onOpenChange={setFromOpen} />
              <DatePickerField testid="booking-until" placeholder="End" value={dateUntil} onSelect={setDateUntil} minDate={dateFrom || startOfToday()} open={untilOpen} onOpenChange={setUntilOpen} />
            </div>
          )}

          {showHowOften && frequency === "one-off" && (
            <Popover open={oneOffOpen} onOpenChange={setOneOffOpen}>
              <PopoverTrigger asChild>
                <button type="button" data-testid="booking-adddays" className={`${fieldBtn} ${oneOffDates.length ? "" : "text-primary"}`}>
                  <CalendarDays size={18} className="text-accent shrink-0" />
                  <span className={`text-base font-medium truncate ${oneOffDates.length ? "text-primary" : "text-primary/75"}`}>{oneOffLabel}</span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="multiple"
                  selected={oneOffDates}
                  onSelect={(d) => setOneOffDates(d || [])}
                  disabled={{ before: startOfToday() }}
                  initialFocus
                />
                <div className="p-3 pt-0">
                  <button
                    type="button"
                    data-testid="booking-adddays-confirm"
                    onClick={() => setOneOffOpen(false)}
                    className="w-full rounded-full bg-primary py-2.5 text-sm font-bold text-primary-foreground hover:-translate-y-0.5 transition-all"
                  >
                    Confirm
                  </button>
                </div>
              </PopoverContent>
            </Popover>
          )}

          {showHowOften && frequency === "weekly" && (
            <div className="grid grid-cols-2 gap-4">
              <button type="button" data-testid="booking-adddays" onClick={() => setDaysOpen(true)} className={fieldBtn}>
                <CalendarDays size={18} className="text-accent shrink-0" />
                <span className={`text-base font-medium truncate ${weekdays.length ? "text-primary" : "text-primary/75"}`}>{weekdaysLabel}</span>
              </button>
              <Popover open={startWeekOpen} onOpenChange={setStartWeekOpen}>
                <PopoverTrigger asChild>
                  <button type="button" data-testid="booking-startweek" className={fieldBtn}>
                    <CalendarDays size={18} className="text-accent shrink-0" />
                    <span className={`text-base font-medium ${startWeek ? "text-primary" : "text-primary/75"}`}>
                      {startWeek ? format(startWeek, "d MMM") : "Start week"}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startWeek}
                    onSelect={setStartWeek}
                    disabled={(date) => date.getDay() !== 1 || date < startOfToday()}
                    initialFocus
                  />
                  <p className="px-4 pb-2 -mt-1 text-xs text-muted-foreground">Mondays only</p>
                  <div className="p-3 pt-0">
                    <button
                      type="button"
                      data-testid="booking-startweek-confirm"
                      onClick={() => setStartWeekOpen(false)}
                      className="w-full rounded-full bg-primary py-2.5 text-sm font-bold text-primary-foreground hover:-translate-y-0.5 transition-all"
                    >
                      Confirm
                    </button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          )}

          {/* Contact details */}
          <div className="pt-4 border-t border-border grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-primary/80">Your name *</Label>
              <Input data-testid="input-owner_name" value={owner.owner_name} onChange={(e) => setO("owner_name", e.target.value)} placeholder="Full name" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-primary/80">Suburb *</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                <Input data-testid="input-suburb" value={owner.suburb} onChange={(e) => setO("suburb", e.target.value)} placeholder="e.g. West End" className={`${inputCls} pl-9`} />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-primary/80">Email *</Label>
              <Input data-testid="input-email" type="email" value={owner.email} onChange={(e) => setO("email", e.target.value)} placeholder="you@email.com" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-primary/80">Mobile (SMS) *</Label>
              <Input data-testid="input-phone" value={owner.phone} onChange={(e) => setO("phone", e.target.value)} placeholder="04XX XXX XXX" className={inputCls} />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-semibold text-primary/80">Anything else we should know?</Label>
            <Textarea data-testid="input-notes" value={owner.notes} onChange={(e) => setO("notes", e.target.value)} placeholder="Our lovable Labrador is in need of a regular walk once a week!" className="bg-background border-border text-primary !text-base placeholder:text-primary/75 focus-visible:ring-ring rounded-xl min-h-[100px]" />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="form-submit-booking"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary py-4 text-base font-bold text-primary-foreground hover:-translate-y-0.5 hover:shadow-xl hover:shadow-primary/25 transition-all disabled:opacity-60 disabled:translate-y-0"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
            {loading ? "Sending request…" : "Request a booking"}
          </button>
          <p className="text-center text-sm text-muted-foreground">You won't be asked to pay yet.</p>
        </motion.form>
      </div>

      {/* Choose service dialog */}
      <Dialog open={serviceOpen} onOpenChange={setServiceOpen}>
        <DialogContent className="bg-card max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-primary" style={{ fontSize: "2rem" }}>Choose your service</DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-sm font-semibold text-muted-foreground">What service are you looking for?</DialogDescription>
          <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
            {SERVICE_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const selected = service?.id === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  data-testid={`service-option-${opt.id}`}
                  onClick={() => setService(opt)}
                  className={`w-full flex items-center gap-4 rounded-2xl border-2 p-4 text-left transition-colors ${
                    selected ? "border-accent bg-secondary/30" : "border-border hover:border-accent/60"
                  }`}
                >
                  <Icon size={26} className={selected ? "text-accent" : "text-primary"} />
                  <div>
                    <div className="font-display text-primary" style={{ fontSize: "1.5rem", lineHeight: 1.1 }}>{opt.name}</div>
                    <div className="text-sm text-muted-foreground font-medium">{opt.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
          <button type="button" data-testid="service-done" onClick={() => setServiceOpen(false)} className="mt-2 w-full rounded-full bg-primary py-3.5 font-bold text-primary-foreground hover:-translate-y-0.5 transition-all">
            Done
          </button>
        </DialogContent>
      </Dialog>

      {/* Add pets dialog */}
      <Dialog open={petsOpen} onOpenChange={setPetsOpen}>
        <DialogContent className="bg-card max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-primary" style={{ fontSize: "2rem" }}>Add your pets</DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-sm font-semibold text-muted-foreground">How many pets do you need to book for?</DialogDescription>
          <div className="divide-y divide-border">
            {PET_ROWS.map((row) => (
              <div key={row.key} className="flex items-center justify-between py-4">
                <div>
                  <div className="font-display text-primary" style={{ fontSize: "1.4rem", lineHeight: 1.1 }}>{row.label}</div>
                  <div className="text-xs text-muted-foreground font-medium">{row.sub}</div>
                </div>
                <div className="flex items-center gap-4">
                  <button type="button" data-testid={`pet-minus-${row.key}`} onClick={() => setPet(row.key, -1)} className="grid place-items-center h-9 w-9 rounded-full bg-muted text-primary hover:bg-secondary/50 transition-colors disabled:opacity-40" disabled={pets[row.key] === 0}>
                    <Minus size={16} />
                  </button>
                  <span data-testid={`pet-count-${row.key}`} className="w-5 text-center font-bold text-primary">{pets[row.key]}</span>
                  <button type="button" data-testid={`pet-plus-${row.key}`} onClick={() => setPet(row.key, 1)} className="grid place-items-center h-9 w-9 rounded-full bg-accent text-accent-foreground hover:brightness-105 transition-all">
                    <Plus size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <button type="button" data-testid="pets-done" onClick={() => setPetsOpen(false)} className="mt-2 w-full rounded-full bg-primary py-3.5 font-bold text-primary-foreground hover:-translate-y-0.5 transition-all">
            Done
          </button>
        </DialogContent>
      </Dialog>

      {/* Which days dialog */}
      <Dialog open={daysOpen} onOpenChange={setDaysOpen}>
        <DialogContent className="bg-card max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-primary" style={{ fontSize: "2rem" }}>Which days?</DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-sm font-semibold text-muted-foreground">Which day(s) would you like to book?</DialogDescription>
          <div className="space-y-1">
            {WEEKDAYS.map((day) => (
              <label key={day} htmlFor={`day-${day}`} className="flex items-center gap-4 py-3 cursor-pointer">
                <Checkbox id={`day-${day}`} data-testid={`weekday-${day}`} checked={weekdays.includes(day)} onCheckedChange={() => toggleDay(day)} className="h-6 w-6 rounded-md border-2 border-border data-[state=checked]:bg-accent data-[state=checked]:border-accent" />
                <span className="text-lg font-medium text-primary">{day}</span>
              </label>
            ))}
          </div>
          <button type="button" data-testid="days-done" onClick={() => setDaysOpen(false)} className="mt-2 w-full rounded-full bg-primary py-3.5 font-bold text-primary-foreground hover:-translate-y-0.5 transition-all">
            Done
          </button>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function DatePickerField({ testid, placeholder, value, onSelect, minDate, open, onOpenChange }) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} className={`${fieldBtn} h-12`}>
          <CalendarDays size={18} className="text-accent shrink-0" />
          <span className={`text-base font-medium ${value ? "text-primary" : "text-primary/75"}`}>
            {value ? format(value, "d MMM") : placeholder}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={value} onSelect={onSelect} disabled={{ before: minDate }} initialFocus />
        <div className="p-3 pt-0">
          <button
            type="button"
            data-testid={`${testid}-confirm`}
            onClick={() => onOpenChange && onOpenChange(false)}
            className="w-full rounded-full bg-primary py-2.5 text-sm font-bold text-primary-foreground hover:-translate-y-0.5 transition-all"
          >
            Confirm
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
